import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Crown, Loader2 } from "lucide-react";

// Simple language helper
export const tr = (language, en, pt, es) => {
  if (language === 'pt') return pt;
  if (language === 'es') return es;
  return en;
};

function TrialFreeBadge({ language }) {
  return (
    <li className="flex items-center gap-2 text-sm text-green-400 font-medium">
      <Sparkles className="w-4 h-4" />
      {tr(language, '30 days FREE with code LAUNCH30!', '30 dias GRÁTIS com código LAUNCH30!', '¡30 días GRATIS con código LAUNCH30!')}
    </li>
  );
}

export function PlanCard({
  variant, // 'free' | 'monthly' | 'yearly'
  plan,
  language,
  isPremium,
  trialUsed,
  loadingPlan,
  onSubscribe,
}) {
  const isFree = variant === 'free';
  const planId = variant;
  const isLoading = loadingPlan === planId;

  // Primary card style per variant
  const cardClass = {
    free: 'bg-zinc-950/50 border-zinc-800',
    monthly: 'bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border-cyan-500/30 relative',
    yearly: 'bg-zinc-950/50 border-zinc-800 relative',
  }[variant];

  const checkColor = {
    free: 'text-zinc-500',
    monthly: 'text-cyan-400',
    yearly: 'text-green-400',
  }[variant];

  // Footer button label
  let primaryButtonLabel;
  if (isFree) {
    primaryButtonLabel = isPremium
      ? tr(language, "You have Premium", "Você tem Premium", "Tienes Premium")
      : plan.cta;
  } else if (isPremium) {
    primaryButtonLabel = tr(language, "Already Subscribed", "Já Assinado", "Ya Suscrito");
  } else {
    primaryButtonLabel = plan.cta;
  }

  const showTrialButton = !isFree && !trialUsed && !isPremium;

  const TitleIcon = variant === 'monthly' ? Sparkles : variant === 'yearly' ? Crown : null;
  const titleIconColor = variant === 'monthly' ? 'text-cyan-400' : 'text-amber-400';

  const description = isFree
    ? tr(language, "To get started", "Para começar", "Para empezar")
    : variant === 'monthly'
      ? tr(language, "Full flexibility", "Flexibilidade total", "Flexibilidad total")
      : tr(language, "Best value", "Melhor valor", "Mejor valor");

  return (
    <Card className={cardClass}>
      {variant === 'monthly' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-cyan-500 text-black font-bold">
            {tr(language, "MOST POPULAR", "MAIS POPULAR", "MÁS POPULAR")}
          </Badge>
        </div>
      )}
      {variant === 'yearly' && plan.savings && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-green-500 text-black font-bold">{plan.savings}</Badge>
        </div>
      )}

      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          {TitleIcon && <TitleIcon className={`w-5 h-5 ${titleIconColor}`} />}
          {plan.name}
        </CardTitle>
        <CardDescription className="text-zinc-400">{description}</CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold text-white">{plan.price}</span>
          <span className="text-zinc-500 ml-2">{plan.period}</span>
        </div>
      </CardHeader>

      <CardContent>
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2 text-sm text-zinc-300">
              <Check className={`w-4 h-4 ${checkColor}`} />
              {feature}
            </li>
          ))}
          {!isFree && !trialUsed && <TrialFreeBadge language={language} />}
        </ul>

        {showTrialButton && (
          <Button
            className="w-full btn-primary mb-2"
            onClick={() => onSubscribe(planId, true)}
            disabled={isLoading}
            data-testid={`start-trial-${planId}`}
          >
            {isLoading
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <Sparkles className="w-4 h-4 mr-2" />}
            {tr(language, 'Start Free Trial', 'Começar Teste Grátis', 'Iniciar Prueba Gratis')}
          </Button>
        )}

        <Button
          variant={variant === 'monthly' ? "default" : "outline"}
          className={
            isFree
              ? "w-full border-zinc-700"
              : variant === 'monthly'
                ? "w-full btn-primary"
                : "w-full bg-transparent border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300"
          }
          onClick={isFree ? undefined : () => onSubscribe(planId, false)}
          disabled={isFree || isPremium || isLoading}
          data-testid={`subscribe-${planId}`}
        >
          {!isFree && isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {primaryButtonLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
